import React, { useState, useEffect } from 'react';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import ConfirmDialog from '@/components/ConfirmDialog';
import { euros } from '@/utils/walletTotals';
import CurrencyService from '@/services/CurrencyService';
import { BACKEND_URL } from '@/config';
import { StatusPill } from '@/components/design';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

export const TRANSFER_LABELS = {
  SCHEDULED: 'Programmé',
  WAITING_ACCOUNT: 'En attente du compte',
  SENDING: 'En cours',
  SENT: 'Versé',
  SIMULATED: 'Simulé',
  FAILED: 'Échec',
  CANCELLED: 'Annulé',
};
const TRANSFER_STATUS_CLASS = {
  SENT: 'completed', SIMULATED: 'completed', FAILED: 'cancelled', CANCELLED: 'cancelled', WAITING_ACCOUNT: 'pending',
};
const RETRYABLE = ['FAILED', 'WAITING_ACCOUNT', 'SCHEDULED'];

export default function AdminTransactions() {
  const toast = useToast();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmDlg, setConfirmDlg] = useState(null); // { action: 'refund'|'release', id }
  // Versements du net aux transporteurs (B11, étape 3)
  const [transfers, setTransfers] = useState([]);
  const [transferBusy, setTransferBusy] = useState('');
  // Encaissements relus chez Stripe, en euros : mesure des frais de conversion supportés par la plateforme (B11, 2026-10-16)
  const [summary, setSummary] = useState(null);

  const loadTransfers = () => axios.get(`${API}/admin/transfers`, { params: { limit: 100 } })
    .then(r => setTransfers(r.data.data || []))
    .catch(() => setTransfers([]));

  useEffect(() => {
    axios.get(`${API}/admin/payments`).then(r => {
      setPayments(r.data.data || []);
      setTotal(r.data.meta?.total || 0);
    }).finally(() => setLoading(false));
    loadTransfers();
    axios.get(`${API}/admin/settlements/summary`).then(r => setSummary(r.data)).catch(() => setSummary(null));
  }, []);

  const retryTransfer = async (missionId) => {
    setTransferBusy(missionId);
    try {
      const { data } = await axios.post(`${API}/admin/transfers/${missionId}/retry`);
      toast.success(`Versement relancé : ${TRANSFER_LABELS[data.transfer?.status] || data.transfer?.status}.`);
      await loadTransfers();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    setTransferBusy('');
  };

  const runTransfers = async () => {
    setTransferBusy('run');
    try {
      const { data } = await axios.post(`${API}/admin/transfers/run`);
      const sent = Object.values(data.run || {}).reduce((a, b) => a + b, 0);
      toast.success(sent ? `${sent} versement(s) traité(s).` : 'Aucun versement à échéance.');
      await loadTransfers();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    setTransferBusy('');
  };

  const doRefund = async () => {
    const id = confirmDlg?.id;
    setConfirmDlg(null);
    try {
      await axios.post(`${API}/payments/${id}/refund`);
      toast.success('Remboursement effectué.');
      window.location.reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const doRelease = async () => {
    const id = confirmDlg?.id;
    setConfirmDlg(null);
    try {
      await axios.post(`${API}/payments/${id}/release`);
      toast.success('Fonds libérés.');
      window.location.reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: 'var(--f-display)', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Transactions</div>
        <div style={{ fontSize: 14, color: 'var(--ink2)' }}>{total} paiements</div>
      </div>

      {/* En euros, d'après les encaissements relus chez Stripe : jamais une somme de devises différentes (D27) */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 24 }}>
        <div className="kpi-card kpi-navy">
          <div className="kpi-label">GMV Total</div>
          <div className="kpi-value" data-testid="admin-kpi-gmv">{euros(summary?.gross_eur)}</div>
          <div className="kpi-sub">Encaissé, en euros</div>
        </div>
        <div className="kpi-card kpi-teal">
          <div className="kpi-label">Revenus commission</div>
          <div className="kpi-value" data-testid="admin-kpi-commission">{euros(summary?.commission_eur)}</div>
          <div className="kpi-sub">Transporteurs, avant frais de Stripe</div>
        </div>
        <div className="kpi-card kpi-teal">
          <div className="kpi-label">Frais Colis2Ship</div>
          <div className="kpi-value" data-testid="admin-kpi-sender-fee">{euros(summary?.sender_fee_eur)}</div>
          <div className="kpi-sub">Payés par les expéditeurs</div>
        </div>
        <div className="kpi-card kpi-gold">
          <div className="kpi-label">À verser</div>
          <div className="kpi-value" data-testid="admin-kpi-held">{euros(summary?.held_eur)}</div>
          <div className="kpi-sub">Net des transporteurs pas encore versé</div>
        </div>
      </div>

      {summary && (
        <div className="card" style={{ marginBottom: 24 }} data-testid="admin-settlements">
          <div style={{ fontFamily: 'var(--f-display)', fontSize: 18, fontWeight: 700 }}>Encaissements en euros</div>
          <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 12 }}>
            Relus chez Stripe sur {summary.missions} mission{summary.missions > 1 ? 's' : ''} ({summary.missions_converted} payée{summary.missions_converted > 1 ? 's' : ''} hors euro)
            {summary.estimates > 0 && <> · {summary.estimates} estimation{summary.estimates > 1 ? 's' : ''} non comptée{summary.estimates > 1 ? 's' : ''}</>}.
            Les frais de conversion (2 %) sont supportés par la plateforme.
          </div>
          <table className="c2s-table">
            <tbody>
              <tr><td>Encaissé</td><td style={{ textAlign: 'right', fontWeight: 700 }} data-testid="admin-settlements-gross">{euros(summary.gross_eur)}</td></tr>
              <tr><td>Frais de carte</td><td style={{ textAlign: 'right' }}>− {euros(summary.card_fee_eur)}</td></tr>
              <tr><td>Frais de conversion</td><td style={{ textAlign: 'right' }} data-testid="admin-settlements-conversion">− {euros(summary.conversion_fee_eur)}</td></tr>
              <tr><td>Net des transporteurs</td><td style={{ textAlign: 'right' }}>− {euros(summary.carrier_net_eur)}</td></tr>
              <tr><td><strong>Reste à la plateforme</strong></td><td style={{ textAlign: 'right', fontWeight: 700 }} data-testid="admin-settlements-platform">{euros(summary.platform_eur)}</td></tr>
            </tbody>
          </table>
          <div style={{ fontSize: 13, marginTop: 12 }} data-testid="admin-settlements-share">
            {summary.conversion_share === null
              ? (summary.conversion_fee_eur > 0 ? 'La plateforme ne garde rien sur ces missions : décision sur les frais de conversion à revoir.' : 'Aucun frais de conversion pour l’instant.')
              : <>La conversion prend {(summary.conversion_share * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} % de ce qui resterait à la plateforme (seuil de révision : {summary.review_share * 100} %).</>}
            {' '}
            {summary.review_due
              ? <span className="status status-cancelled" data-testid="admin-settlements-review">Décision à revoir</span>
              : <span className="status status-completed">Sous le seuil</span>}
          </div>
        </div>
      )}

      <div className="card">
        {loading ? <div className="skeleton" style={{ height: 200 }} /> : (
          <table className="c2s-table">
            <thead>
              <tr><th>ID Mission</th><th>Montant</th><th>Commission</th><th>Devise</th><th>Statut</th><th>Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id}>
                  <td style={{ fontSize: 12, fontFamily: 'monospace' }}>#{p.mission_id?.slice(-8)}</td>
                  <td style={{ fontWeight: 700 }} data-testid={`admin-payment-amount-${p.id}`}>{CurrencyService.formatAmount(p.amount || 0, p.currency || 'EUR')}</td>
                  <td style={{ color: 'var(--teal)', fontWeight: 600 }}>{CurrencyService.formatAmount(p.commission || 0, p.currency || 'EUR')}</td>
                  <td>{p.currency}</td>
                  <td><StatusPill domain="paiement" code={p.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--ink3)' }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {p.status === 'IN_ESCROW' && <button className="btn btn-teal btn-sm" style={{ fontSize: 11 }} onClick={() => setConfirmDlg({ action: 'release', id: p.id })}>Libérer</button>}
                      {['IN_ESCROW', 'PENDING'].includes(p.status) && <button className="btn btn-danger btn-sm" style={{ fontSize: 11 }} onClick={() => setConfirmDlg({ action: 'refund', id: p.id })}>Rembourser</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ marginTop: 24 }} data-testid="admin-transfers">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'var(--f-display)', fontSize: 18, fontWeight: 700 }}>Versements aux transporteurs</div>
            <div style={{ fontSize: 13, color: 'var(--ink2)' }}>
              Net en euros, versé 48 h après la confirmation de livraison. La tâche automatique passe toutes les 15 minutes.
            </div>
          </div>
          <button className="btn btn-teal btn-sm" onClick={runTransfers} disabled={transferBusy === 'run'} data-testid="admin-transfers-run">
            Envoyer maintenant les versements à échéance
          </button>
        </div>
        {transfers.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ink3)' }} data-testid="admin-transfers-empty">Aucun versement pour l'instant.</div>
        ) : (
          <table className="c2s-table">
            <thead>
              <tr><th>ID Mission</th><th>Net (€)</th><th>Statut</th><th>Échéance</th><th>Tentatives</th><th>Dernière erreur</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {transfers.map(m => {
                const t = m.transfer || {};
                return (
                  <tr key={m.id} data-testid={`admin-transfer-${m.id}`}>
                    <td style={{ fontSize: 12, fontFamily: 'monospace' }}>#{m.id?.slice(-8)}</td>
                    <td style={{ fontWeight: 700 }}>{(t.amount_eur ?? m.settlement?.carrier_net_eur)?.toFixed?.(2) ?? '·'}</td>
                    <td><span className={`status status-${TRANSFER_STATUS_CLASS[t.status] || 'pending'}`}>{TRANSFER_LABELS[t.status] || t.status}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--ink3)' }}>{t.due_at ? new Date(t.due_at).toLocaleString('fr-FR') : '·'}</td>
                    <td>{t.attempts || 0}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink3)', maxWidth: 260, overflowWrap: 'anywhere' }}>{t.last_error || '·'}</td>
                    <td>
                      {m.status === 'COMPLETED' && RETRYABLE.includes(t.status) && (
                        <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => retryTransfer(m.id)}
                          disabled={transferBusy === m.id} data-testid={`admin-transfer-retry-${m.id}`}>Relancer</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        open={confirmDlg?.action === 'release'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doRelease}
        variant="financial"
        icon="coin"
        title="Libérer les fonds ?"
        subtitle="Décision définitive : le versement du net au transporteur partira aussitôt."
        confirmLabel="Confirmer la libération"
      />
      <ConfirmDialog
        open={confirmDlg?.action === 'refund'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doRefund}
        variant="destructive"
        icon="ban"
        title="Confirmer le remboursement ?"
        subtitle="Le paiement Stripe sera remboursé à l'expéditeur."
        confirmLabel="Confirmer le remboursement"
      />
    </div>
  );
}
